import React, { useState } from 'react'
import request from '@/modules/base/util/request'
import lang from '@/lang'
import moment from 'moment'
import clsx from 'clsx'
import config from '@/config'
import settingsConfig from '@/modules/settings/config'
import { makeStyles } from '@mui/styles'
import { DateInput } from '@/modules/base/components/inputs'
import { ButtonIconSquare } from '@/modules/base/components/buttons'
import IconCheck from 'assets/images/icons/check.svg'

import { toast } from '@/modules/base/components/toast/toastConfig'
import { convertDateToApiDateFormat } from '@/modules/_helpers/formatDate'
import useCheckAccessRights from '@/modules/_helpers/accessCheck/useCheckAccessRights'
import { USER_RIGHTS } from '@/modules/base/util/constants'

const BookkeepingAccountRecords = () => {
  const classes = useStyles()
  const [fromDate, setFromDate] = useState(convertDateToApiDateFormat(moment().startOf('year')))
  const [toDate, setToDate] = useState(convertDateToApiDateFormat(moment()))

  const bankingBookingWriteRight = useCheckAccessRights(USER_RIGHTS.ACCOUNTING.BANKING_BOOKING)

  const onDateChange = (value, name) => {
    const newDate = convertDateToApiDateFormat(value)
    if (name === 'fromDate') {
      setFromDate(newDate)
    } else {
      setToDate(newDate)
    }
  }

  const onButtonClick = () => {
    request(settingsConfig.bookkeepingRecreateUrl, {
      timeout: config.requestExtraLongTimeout,
      auth: true,
      method: 'POST',
      data: {
        fromDate: moment(fromDate).add(moment().utcOffset(), 'minutes').toISOString(),
        toDate: moment(toDate).add(moment().utcOffset(), 'minutes').toISOString(),
      },
    }).then((response) => {
      const state = response?.body?.data?.transactionState
      if (state === 'succeeded') {
        toast.showToast({ type: 'info', message: lang.bookkeepingAccount.done })
      }
      if (state === 'running' || state === 'queued') {
        toast.showToast({ message: lang.bookkeepingAccount.started })
      }
      if (state === 'already_running') {
        toast.showToast({ message: lang.bookkeepingAccount.inProgress })
      }
    }).catch((error) => {
      if (error.code !== 'ETIMEDOUT') {
        toast.showToast({ message: lang.bookkeepingAccount.error })
      } else {
        toast.showToast({ message: lang.bookkeepingAccount.longer })
      }
    })
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.title}>{lang.bookkeepingAccount.title}</div>
      <div className={classes.subtitle}>{lang.bookkeepingAccount.subtitle}</div>
      <div className={classes.row}>
        <div className={classes.column}>
          <DateInput
            name='fromDate'
            readOnly={!bankingBookingWriteRight}
            label={lang.timeTracking.startDateLabel}
            customClass={classes.column}
            value={fromDate}
            onChange={onDateChange}
            onBlur={onDateChange}
          />
          <DateInput
            name='toDate'
            readOnly={!bankingBookingWriteRight}
            label={lang.timeTracking.endDateLabel}
            customClass={classes.column}
            value={toDate}
            onChange={onDateChange}
            onBlur={onDateChange}
          />
        </div>
      </div>
      <div className={classes.buttonContainer}>
        <ButtonIconSquare
          label={lang.bookkeepingAccount.recreateButtonLabel}
          icon={IconCheck}
          type='submit'
          onClick={onButtonClick}
          disabled={!bankingBookingWriteRight || !fromDate || !toDate}
          dataQsId='settings-mycompany-bookkeeping-accountRecord-save'
        />
      </div>
    </div>
  )
}

const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    fontWeight: theme.fontWeights.medium,
    fontSize: theme.fontSizes.smedium,
    marginBottom: 30,
  },
  row: {
    ...theme.mixins.formRow(),
    alignItems: 'flex-end',
    paddingTop: 20,
  },
  column: {
    display: 'flex',
    ...theme.mixins.formRowColumn('0.5rem'),
    maxWidth: '100%',
  },
  buttonContainer: {
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-end',
  },
}))

export default BookkeepingAccountRecords
